import TabbedDialog from 'global/guides/Dialog/TabbedDialog'
import { hasPermission } from 'hooks/useBackendMenu'
import { useFuncionariosOutletContext } from 'pages/Funcionarios'
import { queryClient } from 'providers/ReactQuery'
import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import {
    Funcionario,
    FuncionarioCentroCusto,
    calculo,
    funcionario,
    funcionarioCentroCustoSchema,
} from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { AdicionaisForm } from '../forms/Adicionais'
import { CentroCustoForm } from '../forms/CentroCusto'
import { DadosPessoaisForm } from '../forms/DadosPessoais'
import FormExcluirCalculo from '../forms/FormExcluirCalculo'
import TabCalendario from '../forms/TabelaHorario/TabTabelaHorario'
import { schema as adicionarSchema, getRascunhoFuncionario } from './Adicionar'
import { z } from 'zod'

const schema = adicionarSchema
    .required({
        id: true,
    })
    .extend({
        repSenha: z
            .string()
            .nullish()
            .or(z.literal(''))
            .transform(val => (val === '' || val === undefined ? null : val)),

        FuncionarioCentroCusto: funcionarioCentroCustoSchema
            .deepPartial()
            .array()
            .nullish()
            .or(funcionarioCentroCustoSchema.deepPartial().nullish())
            .refine(
                data => {
                    if (Array.isArray(data))
                        return data.every(item =>
                            item.CentroCusto ? !!item.dtInicial : true,
                        )
                    else if (data?.CentroCusto) return !!data.dtInicial
                    return true
                },
                {
                    message: 'Campo obrigatório',
                    path: ['dtInicial'],
                },
            ),
    })

export default function EditarFuncionario() {
    const navigate = useNavigate()
    const [searchParam] = useSearchParams()
    const tab = searchParam.get('aba')
    const [rascunho] = useState(() => getRascunhoFuncionario())
    const [submittedValues, setSubmitValues] = useState<Funcionario>()
    const { funcionarioId } = useParams()
    const { data } = useFuncionariosOutletContext()
    const row = data?.find(item => item.id === Number(funcionarioId))!
    const empresaId = useStore(state => state.companyId)

    const { mutateAsync, isLoading } = funcionario.mutations.useUpdate({
        queryParams: { id: funcionarioId },
        invalidateQuery: true,
    })

    function handleSubmit(
        data: Omit<Funcionario, 'FuncionarioCentroCusto'> & {
            apagarCalculo?: boolean
            FuncionarioCentroCusto?:
                | FuncionarioCentroCusto
                | FuncionarioCentroCusto[]
        },
    ) {
        if (
            !Array.isArray(data?.FuncionarioCentroCusto) &&
            !data?.FuncionarioCentroCusto?.CentroCusto
        ) {
            delete data.FuncionarioCentroCusto
        }

        return mutateAsync(data, {
            onSuccess: () => {
                toast.success('Funcionário atualizado com sucesso!')

                queryClient.invalidateQueries(
                    funcionario.queryKey({ empresaId }),
                )

                queryClient.invalidateQueries([
                    'centro-custos-funcionario',
                    funcionarioId,
                ])
            },
            onError: err => {
                toast.error(err.message)
            },
        })
    }

    async function verificarCalculo(values: Funcionario) {
        if (!values.dtRescisao) {
            await handleSubmit({
                ...values,
                apagarCalculo: false,
            })
            return navigate(-1)
        }

        const existeCalculo: number[] = await calculo.get('fora-periodo', {
            funcionarioId: row.id,
            dataInicial: values.dtAdmissao,
            dataFinal: values.dtRescisao,
        })

        if (!existeCalculo.length) {
            await handleSubmit({ ...values, apagarCalculo: false })
            return navigate(-1)
        }

        setSubmitValues(values)
    }

    useEffect(() => {
        if (Object.values(rascunho).length)
            localStorage.removeItem('@rascunho:funcionario')
    }, [rascunho])

    if (!row) return null

    return (
        <TabbedDialog.Root
            tabs={[
                {
                    name: 'dados',
                    label: 'Dados básicos',
                    content: <DadosPessoaisForm editar />,
                },
                {
                    name: 'adicionais',
                    label: 'Adicionais',
                    content: <AdicionaisForm />,
                },
                {
                    name: 'centro-custo',
                    label: 'Centro de Custo',
                    content: <CentroCustoForm />,
                },
                {
                    name: 'tabela-horario',
                    label: 'Horário',
                    content: <TabCalendario />,
                },
            ]}
            schema={schema}
            open={hasPermission(['update', 'Funcionários'])}
            loading={isLoading}
            onSubmit={verificarCalculo}
            defaultTab={tab || undefined}
            defaultValues={{
                ...row,
                regraCalculo: rascunho.regraCalculo ?? row.regraCalculo,
                tabelaHorarioId: row.TabelaHorario?.id,
                uf: row.Cidade?.uf,
            }}
        >
            <TabbedDialog.Title>Editar funcionário</TabbedDialog.Title>
            <TabbedDialog.Content dividers />
            <FormExcluirCalculo
                {...{
                    submittedValues,
                    clearValues: () => setSubmitValues(undefined),
                    handleSubmit,
                }}
            />
            <TabbedDialog.Actions>
                <TabbedDialog.Actions.SecondaryButton />
                <TabbedDialog.Actions.PrimaryButton />
            </TabbedDialog.Actions>
        </TabbedDialog.Root>
    )
}
